import React from 'react';
import { Button, Card, Space, Typography, Divider } from 'antd';
import * as Sentry from '@sentry/react';
import {
  BugOutlined,
  MessageOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

/**
 * Component to test Sentry integration
 * Should only be used in development environment
 */
const SentryTest: React.FC = () => {
  const handleTestError = () => {
    try {
      throw new Error('Test Error - This is a test error from button');
    } catch (error) {
      Sentry.captureException(error);
      // console.log('Test error sent to Sentry');
    }
  };

  const handleTestMessage = () => {
    Sentry.captureMessage('Test Message - Test message from application', 'info');
    // console.log('Test message sent to Sentry');
  };

  const handleSetUser = () => {
    Sentry.setUser({
      id: '12345',
      email: 'test@example.com',
      username: 'test_user',
    });
    // console.log('User context has been set');
  };

  const handleTestErrorBoundary = () => {
    // Throw error to test Error Boundary
    throw new Error('Test Error Boundary - Error to test Error Boundary');
  };

  const handleAddBreadcrumb = () => {
    Sentry.addBreadcrumb({
      category: 'test',
      message: 'User clicked test button',
      level: 'info',
      timestamp: Date.now() / 1000,
    });
    // console.log('Breadcrumb added');
  };

  const handleTestWarning = () => {
    Sentry.captureMessage('Test Warning - Test warning message', 'warning');
    // console.log('Warning sent to Sentry');
  };

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <Card>
        <Title level={2}>
          <BugOutlined /> Test Sentry Integration
        </Title>
        <Text type="secondary">
          Use the buttons below to test Sentry features. Check the console and
          Sentry dashboard to see the results.
        </Text>

        <Divider />

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Test Error Capture */}
          <Card size="small" title="1. Test Error Capture">
            <Text>Capture an exception and send it to Sentry</Text>
            <br />
            <Button
              type="primary"
              icon={<BugOutlined />}
              onClick={handleTestError}
              style={{ marginTop: '8px' }}
            >
              Test Error
            </Button>
          </Card>

          {/* Test Message */}
          <Card size="small" title="2. Test Message">
            <Text>Send a message to Sentry</Text>
            <br />
            <Button
              icon={<MessageOutlined />}
              onClick={handleTestMessage}
              style={{ marginTop: '8px' }}
            >
              Test Message
            </Button>
          </Card>

          {/* Test User Context */}
          <Card size="small" title="3. Test User Context">
            <Text>Set user information to track in Sentry</Text>
            <br />
            <Button
              icon={<UserOutlined />}
              onClick={handleSetUser}
              style={{ marginTop: '8px' }}
            >
              Set User Context
            </Button>
          </Card>

          {/* Test Breadcrumb */}
          <Card size="small" title="4. Test Breadcrumb">
            <Text>Add breadcrumb to track user actions</Text>
            <br />
            <Button onClick={handleAddBreadcrumb} style={{ marginTop: '8px' }}>
              Add Breadcrumb
            </Button>
          </Card>

          {/* Test Warning */}
          <Card size="small" title="5. Test Warning">
            <Text>Send warning message to Sentry</Text>
            <br />
            <Button
              icon={<WarningOutlined />}
              onClick={handleTestWarning}
              style={{ marginTop: '8px' }}
            >
              Test Warning
            </Button>
          </Card>

          {/* Test Error Boundary */}
          <Card size="small" title="6. Test Error Boundary">
            <Text type="danger">
              ⚠️ This button will crash the app to test Error Boundary. The
              page will display an error fallback UI.
            </Text>
            <br />
            <Button
              danger
              icon={<BugOutlined />}
              onClick={handleTestErrorBoundary}
              style={{ marginTop: '8px' }}
            >
              Test Error Boundary (Crashes App)
            </Button>
          </Card>
        </Space>

        <Divider />

        <Text type="secondary">
          <strong>Note:</strong> After clicking the buttons, check:
          <ul>
            <li>Console log in the browser</li>
            <li>
              Network tab to see requests sent to Sentry (filter: sentry.io)
            </li>
            <li>
              Sentry Dashboard at{' '}
              <a
                href="https://sentry.io"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://sentry.io
              </a>
            </li>
          </ul>
        </Text>
      </Card>
    </div>
  );
};

export default SentryTest;
